import * as React from "react";
import { Printer } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "convex/react";

import { Button } from "@/components/ui/button";
import { useSession } from "@/lib/auth-client";
import { formatUtcDateOnly } from "@/lib/format";
import { calculatePalletCountForDelivery } from "@/lib/pallet-policy";
import { api } from "$convex/convexApi";
import "./page.css";


const ANTHEM_LOGO_URL =
  "https://cdn.prod.website-files.com/64493d11d62bbdf4ac3b43ef/6449f415b06610516258fd38_logo-mark.webp";

function formatDate(timestamp?: number | string): string {
  if (!timestamp) return "-";
  const date = typeof timestamp === "number" ? new Date(timestamp) : new Date(timestamp);
  return date
    .toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "2-digit",
    })
    .replace(/\//g, ".");
}

function formatScheduledDate(timestamp?: number): string {
  if (!timestamp) return "-";
  return formatUtcDateOnly(timestamp, {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  }).replace(/\//g, ".");
}

function formatCityStateZip(city?: string, state?: string, zip?: string): string {
  if (!city && !state && !zip) return "-";
  return `${city || ""}, ${state || ""}, ${zip || ""}`
    .replace(/,\s*,/g, ",")
    .replace(/^,\s*/, "")
    .replace(/,\s*$/, "");
}

function formatFullName(firstName?: string, lastName?: string): string {
  if (!firstName && !lastName) return "-";
  return `${firstName || ""} ${lastName || ""}`.trim();
}

function getAccessorialLabel(accessorial?: {
  description?: string | null;
  name?: string | null;
}): string | null {
  const rawLabel = accessorial?.description ?? accessorial?.name;
  if (typeof rawLabel !== "string") return null;
  const label = rawLabel.trim();
  return label.length > 0 ? label : null;
}

function calculatePalletHeight(bagsPerPallet: number): number {
  if (bagsPerPallet <= 2) return 12;
  if (bagsPerPallet <= 4) return 20;
  if (bagsPerPallet <= 6) return 30;
  if (bagsPerPallet <= 8) return 40;
  if (bagsPerPallet <= 10) return 48;
  return Math.min(54, 48 + (bagsPerPallet - 10) * 4);
}

export default function BolPage() {
  const [searchParams] = useSearchParams();
  const orderNumber = searchParams.get("orderNumber") ?? searchParams.get("ordernumber");
  const orderId = searchParams.get("orderId") ?? searchParams.get("orderid");

  const { data: session, isPending: isAuthLoading } = useSession();
  const authUserId = (session?.user as { id?: string } | undefined)?.id;

  const orderData = useQuery(
    api.admin.getOrderWithFullDetails,
    !isAuthLoading && authUserId && (orderId || orderNumber)
      ? {
          authUserId,
          ...(orderId ? { orderId: orderId as any } : {}),
          ...(orderNumber ? { orderNumber } : {}),
        }
      : "skip",
  ) as any;

  const storage = useQuery(api.customerStorage.getForOrder,
    authUserId && orderData?._id ? { orderId: orderData._id } : "skip");
  const isLoading = Boolean(
    isAuthLoading || (authUserId && (orderId || orderNumber) && orderData === undefined) ||
    (authUserId && orderData?._id && storage === undefined),
  );

  const handlePrint = React.useCallback(() => {
    window.print();
  }, []);

  const user = orderData?.user;
  const items = orderData?.items ?? [];
  const delivery = orderData?.delivery;
  const shippingInfo = orderData?.shippingInfo;
  const accessorials = orderData?.accessorials ?? [];
  const orderMeta = orderData?.metadata as
    | { phoneNumber?: string; additionalData?: { phoneNumber?: string } }
    | undefined;
  const accessorialNotificationPhone =
    orderMeta?.phoneNumber ?? orderMeta?.additionalData?.phoneNumber;
  const selectedShippingService = String(delivery?.selectedService ?? delivery?.serviceName ?? "").trim();
  const hasGuaranteedShippingService =
    delivery?.guaranteedService === true || /\bguaranteed\b/i.test(selectedShippingService);
  const guaranteedShippingServiceLabel = selectedShippingService || "Guaranteed shipping service";

  const bolDate = typeof delivery?.deliveryDate === "number"
    ? formatScheduledDate(delivery.deliveryDate)
    : typeof delivery?.pickupDate === "number"
      ? formatScheduledDate(delivery.pickupDate)
      : formatDate(orderData?.createdAt || orderData?._creationTime);
  const fullName = formatFullName(user?.firstName, user?.lastName);

  const addressDisplay = shippingInfo
    ? formatCityStateZip(shippingInfo.city, shippingInfo.state, shippingInfo.postalCode)
    : formatCityStateZip(user?.city, user?.state, user?.postalCode);

  const addressString = shippingInfo?.address || user?.shippingAddress || user?.billingAddress || "-";

  const totalBags = items.reduce((sum: number, item: any) => sum + (Number(item?.quantity) || 0), 0);
  const totalPallets = calculatePalletCountForDelivery(totalBags, delivery);

  const totalProductWeight = items.reduce((sum: number, item: any) => {
    const productWeight = item.product?.weight || 0;
    return sum + productWeight * (item.quantity || 0);
  }, 0);

  const palletWeight = 42;
  const palletTotalWeight = palletWeight * totalPallets;
  const totalWeight = totalProductWeight + palletTotalWeight;

  const hasOrganic = items.some(
    (item: any) => item.product?.certificationId === 1 || item.options?.isOrganic,
  );

  const palletHeight =
    totalBags && totalPallets
      ? calculatePalletHeight(Math.ceil(totalBags / totalPallets))
      : 48;

  const cubicFt = (48 * 40 * palletHeight * totalPallets) / 1728;
  const pcf = totalWeight > 0 && cubicFt > 0 ? totalWeight / cubicFt : 0;
  const freightClass = totalBags <= 2 ? "70" : "65";

  React.useEffect(() => {
    const previousTitle = document.title;
    document.title = "BOL";

    let robotsMeta = document.querySelector("meta[name='robots']") as HTMLMetaElement | null;
    const hadRobotsMeta = Boolean(robotsMeta);
    if (!robotsMeta) {
      robotsMeta = document.createElement("meta");
      robotsMeta.setAttribute("name", "robots");
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute("content", "noindex, nofollow");

    return () => {
      document.title = previousTitle;
      if (robotsMeta && !hadRobotsMeta) {
        robotsMeta.remove();
      }
    };
  }, []);

  if (!orderNumber && !orderId) {
    return (
      <main className="bol-page">
        <div className="error-container">
          <h2>Error</h2>
          <p>Order number or order ID is required. Use ?orderNumber=XXX or ?orderId=XXX</p>
        </div>
      </main>
    );
  }

  if (isLoading) {
    return (
      <main className="bol-page">
        <div className="loading-container">
          <p>Loading BOL data...</p>
        </div>
      </main>
    );
  }

  if (!authUserId) {
    return (
      <main className="bol-page">
        <div className="error-container">
          <h2>Authentication Required</h2>
          <p>Please sign in to view this bill of lading.</p>
        </div>
      </main>
    );
  }

  if (!orderData) {
    return (
      <main className="bol-page">
        <div className="error-container">
          <h2>Not Found</h2>
          <p>Order not found</p>
        </div>
      </main>
    );
  }

  const storageBlocksShipping = storage && (storage.sandbox || !storage.fundingCurrent ||
    (storage.kind === "purchase" && storage.holdStatus !== "released") ||
    (storage.kind === "release" && storage.warehouseStatus !== "stored"));
  if (orderData.test === true || orderData.status === "held_for_customer" || storageBlocksShipping) {
    return <main className="bol-page"><div className="error-container" role="alert">
      <h2>Shipping paperwork unavailable</h2>
      <p>{orderData.test === true ? "Test orders cannot issue a bill of lading." : "This coffee is in customer storage. Use the holding DO until a release order is ready."}</p>
      <a href={`/order-resume?orderId=${encodeURIComponent(orderData._id)}&purpose=holding`}>View current DO</a>
    </div></main>;
  }

  return (
    <main className="bol-page">
      <div className="print-controls">
        <Button type="button" onClick={handlePrint}>
          <Printer className="mr-2 h-4 w-4" />
          Print
        </Button>
      </div>
      <div className="bol-container">
        <div className="bol-header">
          <h4>BILL OF LADING</h4>
          <img src={ANTHEM_LOGO_URL} alt="Anthem Coffee Logo" className="bol-logo" />
        </div>

        <div className="row">
          <div className="col">
            <div>
              <div className="section-title">
                <span>SHIPPER</span>
              </div>
              <div>
                <span>Anthem Coffee Imports</span>
              </div>
              <div>
                <span>13623 Wyandotte St.</span>
              </div>
              <div>
                <span>Kansas City, MO, 64145, USA</span>
              </div>
              <div>
                <span>JV Sanoubane (816)666-0225</span>
              </div>
              <div>
                <span>traffic@anthemcoffeeimports.com</span>
              </div>
            </div>
            <div>
              <div className="section-title">
                <span>CONSIGNEE</span>
              </div>
              <div className="highlight">{user?.company || "-"}</div>
              <div className="highlight">{addressString}</div>
              <div className="highlight">{addressDisplay}</div>
              <div className="highlight">{fullName}</div>
              <div className="highlight">{user?.mainPhoneNumber || "-"}</div>
              <div className="highlight">{user?.email || "-"}</div>
            </div>
          </div>

          <div className="col">
            <div className="pro-number-box">
              <strong>PLEASE AFFIX PRO NUMBER HERE</strong>
            </div>
            <div className="row flex">
              <div className="col">
                <div className="carrier-logo-box">
                  {delivery?.carrierLogoUrl ? (
                    <img src={delivery.carrierLogoUrl} alt={delivery.carrierName || "Carrier"} className="carrier-logo" />
                  ) : null}
                </div>
              </div>
              <div className="col">
                <div>
                  <strong>Carrier</strong>
                  <div className="highlight">{delivery?.carrierName || "-"}</div>
                </div>
                <div>
                  <strong>Carrier PRO#:</strong>
                  <div className="highlight">{delivery?.trackingNumber || "-"}</div>
                </div>
              </div>
              <div className="col">
                <div>
                  <strong>Pickup Date</strong>
                  <div className="highlight">{bolDate}</div>
                </div>
                <div>
                  <strong>Bill of Lading #:</strong>
                  <div className="highlight">{orderData.orderNumber || "-"}</div>
                </div>
              </div>
              <div className="col">
                <div className="section-title">
                  <span>Freight Charge Terms</span>
                </div>
                <div className="checkboxes">
                  <div className="checkbox">
                    <div className="checkbox-box checked" />
                    <span>Prepaid</span>
                  </div>
                  <div className="checkbox">
                    <div className="checkbox-box" />
                    <span>Collect</span>
                  </div>
                  <div className="checkbox">
                    <div className="checkbox-box" />
                    <span>Third Party</span>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <strong>QUOTE #:</strong>
              <span className="highlight">{delivery?.quoteId || "-"}</span>
            </div>
            {hasGuaranteedShippingService ? (
              <div>
                <strong>Guaranteed Service:</strong>
                <span className="highlight"> {guaranteedShippingServiceLabel}</span>
              </div>
            ) : null}
          </div>
        </div>

        <div className="row">
          <div className="col">
            <div>
              <div className="section-title">
                <span>PICKUP REMARKS</span>
              </div>
              <div>
                Ready from <strong>1:00PM</strong> to <strong>5:00PM</strong>
              </div>
            </div>
          </div>
          <div className="col">
            <div>
              <div className="section-title">
                <span>DELIVERY REMARKS</span>
              </div>
              <div>
                <strong>Accessorials: </strong>
                {accessorials.length > 0 ? (
                  accessorials.map((accessorial: any) => {
                    // Match legacy BOL (AnthemTan): short service label only — never the checkout question copy.
                    const label = getAccessorialLabel(accessorial);
                    if (!label) return null;
                    const suffix =
                      label === "Call Ahead" && accessorialNotificationPhone
                        ? ` ${accessorialNotificationPhone}`
                        : "";
                    return (
                      <div key={accessorial._id ?? label}>
                        {label}
                        {suffix}
                      </div>
                    );
                  })
                ) : (
                  <div>None</div>
                )}
              </div>
              <div style={{ marginTop: 10 }}>
                <strong>Customer Notes:</strong>
                <div className="customer-notes-box" style={{ marginTop: 5 }}>
                  {orderData.specialInstructions ? orderData.specialInstructions : <span className="no-notes" />}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="section-title">
            <span>SPECIAL INSTRUCTIONS</span>
          </div>
          <div>
            <span>
              Food Grade. Do not double stack or load with odorous substances. All authorized accessorials are noted on the BOL.
              Anthem will not accept any further unauthorized accessorial charges.
            </span>
            {hasOrganic ? (
              <span className="highlight">
                <strong> Including Organic.</strong>
              </span>
            ) : null}
          </div>
        </div>

        <div className="section">
          <table>
            <thead>
              <tr>
                <th colSpan={2}>Handling Unit</th>
                <th colSpan={2}>Package</th>
                <th rowSpan={2}>Commodity Description</th>
                <th rowSpan={2}>Weight*</th>
                <th rowSpan={2}>Freight Class</th>
                <th rowSpan={2}>NMFC#</th>
                <th colSpan={3}>Dimensions</th>
                <th rowSpan={2}>Stackable?</th>
              </tr>
              <tr>
                <th>Type</th>
                <th>QTY</th>
                <th>Type</th>
                <th>QTY</th>
                <th>L</th>
                <th>W</th>
                <th>H</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>PALLET</td>
                <td className="highlight">{totalPallets}</td>
                <td>Bag / Box</td>
                <td className="highlight">{totalBags}</td>
                <td>
                  <span>green coffee</span>
                  {hasOrganic ? <span className="highlight"> organic</span> : null}
                </td>
                <td className="highlight">{totalProductWeight.toFixed(2)}</td>
                <td className="highlight">{freightClass}</td>
                <td />
                <td>48</td>
                <td>40</td>
                <td className="highlight">{palletHeight}</td>
                <td>NO</td>
              </tr>
              <tr>
                <td>Total H/U</td>
                <td className="highlight">{totalPallets}</td>
                <td>Total Pkg</td>
                <td className="highlight">{totalBags}</td>
                <td />
                <td />
                <td className="highlight">{freightClass}</td>
                <td>Density</td>
                <td className="highlight">{pcf.toFixed(2)}</td>
                <td colSpan={2}>Cube</td>
                <td>{cubicFt.toFixed(2)}</td>
              </tr>
              <tr>
                <td colSpan={5} />
                <td>
                  <table style={{ border: "none", width: "100%" }}>
                    <tbody>
                      <tr>
                        <td style={{ textAlign: "left", border: "none", padding: 0 }}>Sub-Total</td>
                        <td style={{ textAlign: "right", border: "none", padding: 0 }}>{totalProductWeight.toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </table>
                </td>
                <td colSpan={6} />
              </tr>
              <tr>
                <td colSpan={5} />
                <td>
                  <table style={{ border: "none", width: "100%" }}>
                    <tbody>
                      <tr>
                        <td style={{ textAlign: "left", border: "none", padding: 0 }}>
                          <strong>Pallet Wt</strong>
                        </td>
                        <td style={{ textAlign: "right", border: "none", padding: 0 }}>
                          <strong>{palletTotalWeight.toFixed(2)}</strong>
                        </td>
                      </tr>
                      <tr>
                        <td style={{ textAlign: "left", border: "none", padding: 0 }}>
                          <strong>Total Wt</strong>
                        </td>
                        <td style={{ textAlign: "right", border: "none", padding: 0 }}>
                          <strong>{totalWeight.toFixed(2)}</strong>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>
                <td colSpan={6} />
              </tr>
            </tbody>
          </table>
          <div>
            <small>
              * Weights are total for number of handling units stated. Do not multiply weight by number of handling units.
            </small>
          </div>
        </div>

        <div className="signatures">
          <div className="row">
            <div className="col">
              <div className="signature-title">
                <span>Shipper signature/date</span>
              </div>
              <div className="signature-line" />
              <div className="disclaimer">
                <span>
                  §172.204 This is to certify that the above-named materials are properly classified, described, packaged,
                  marked and labeled, and are in proper condition for transportation according to the applicable regulations of
                  the Department of Transportation.
                </span>
              </div>
              <div>
                <div className="signature-title">
                  <span>Signature</span>
                </div>
                <div className="signature-line" />
              </div>
              <div>
                <div className="signature-title">
                  <span>Date</span>
                </div>
                <div className="signature-line" />
              </div>
            </div>
            <div className="col">
              <div className="row">
                <div className="col">
                  <div className="signature-title">
                    <span>Trailer Loaded:</span>
                  </div>
                  <div className="checkbox">
                    <div className="checkbox-box" />
                    <span>By Shipper</span>
                  </div>
                  <div className="checkbox">
                    <div className="checkbox-box" />
                    <span>By Driver</span>
                  </div>
                </div>
                <div className="col">
                  <div className="signature-title">
                    <span>Freight Counted:</span>
                  </div>
                  <div className="checkbox">
                    <div className="checkbox-box" />
                    <span>By Shipper</span>
                  </div>
                  <div className="checkbox">
                    <div className="checkbox-box" />
                    <span>By Driver / Pallets said to contain</span>
                  </div>
                  <div className="checkbox">
                    <div className="checkbox-box" />
                    <span>By Driver / Pieces</span>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <div className="signature-title">
                    <span>H/U Count:</span>
                  </div>
                  <div className="signature-line" />
                </div>
                <div className="col">
                  <div className="signature-title">
                    <span>Piece Count:</span>
                  </div>
                  <div className="signature-line" />
                </div>
              </div>
            </div>
            <div className="col">
              <div className="signature-title">
                <span>Carrier signature/date</span>
              </div>
              <div className="signature-line" />
              <div className="disclaimer">
                <span>
                  Carrier acknowledges receipt of packages and required placards / Carriers certifies emergency response
                  information was made available and / or carrier has the DOT emergency response guidebook or equivalent
                  documentation in the vehicle.
                </span>
              </div>
              <div>
                <div className="signature-title">
                  <span>Signature</span>
                </div>
                <div className="signature-line" />
              </div>
              <div>
                <div className="signature-title">
                  <span>Date</span>
                </div>
                <div className="signature-line" />
              </div>
            </div>
          </div>
        </div>

        <div className="footer">
          <strong>
            NOTE: Liability Limitation for loss or damage in this shipment may be applicable. See 49 U.S.C. 14706(c)(1)(A) and
            (B)
          </strong>
        </div>
      </div>
    </main>
  );
}
